/**
 * KanbanView.tsx - KanbanView - Dataset-aware Kanban board component
 * 
 * CHANGE HISTORY:
 * - 251021_0438 [46df53c] danielbeer: feat: complete comprehensive package migration to full standards
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { useDatasetView } from '../base/useDatasetView';
import { useBacktrackr } from '@opcentral/backtrackr';
import { useViewMetadata } from '../base/useViewMetadata';
import KanbanColumn from './KanbanColumn';
import './KanbanView.css';

/**
 * KanbanView Component
 * 
 * @param {Object} props
 * @param {string} props.viewId - Unique view identifier
 * @param {Object} props.datasetContext - Dataset context from useDataset()
 * @param {Object} props.dataContext - Data context from useData()
 * @param {Array} props.items - Optional items array (fallback if context not available)
 * @param {Function} props.onItemClick - Callback when item is clicked
 * @param {Function} props.onItemUpdate - Callback when item is updated
 * @param {Array} props.columns - Column configuration (optional, defaults to standard statuses)
 * @param {Object} props.columnStyles - Custom styles for columns
 * @param {Object} props.cardStyles - Custom styles for cards
 */
const KanbanView = ({ 
  viewId = 'kanban-default',  // NEW: Unique view identifier
  datasetContext, 
  dataContext,
  backtrackrContext: propBacktrackrContext,
  items: propItems,
  onItemClick,
  onItemUpdate,
  columns: customColumns,
  columnStyles = {},
  cardStyles = {}
}) => {
  const [activeId, setActiveId] = useState(null);
  
  // Use Backtrackr for undo/redo
  const hookBacktrackrContext = useBacktrackr();
  const backtrackrContext = propBacktrackrContext || hookBacktrackrContext;
  const { recordChange, undo, redo, canUndo, canRedo } = backtrackrContext || {};
  
  // Use dataset view hook
  const {
    fieldMappings,
    getField,
    setField,
    items: contextItems,
    addItem,
    updateItem
  } = useDatasetView('kanban', datasetContext, dataContext);
  
  // Use prop items if provided, otherwise use context items
  const items = propItems || contextItems;
  
  // NEW: Use view metadata hook for view-specific data
  const {
    getSortOrder,
    setSortOrder,
    getSortedItems
  } = useViewMetadata(viewId, items, updateItem);
  
  // Default column configuration
  const defaultColumns = [
    { id: 'active', title: 'Active', color: 'var(--color-info)' },
    { id: 'todo', title: 'To Do', color: '#94a3b8' },
    { id: 'in-progress', title: 'In Progress', color: 'var(--color-warning)' },
    { id: 'done', title: 'Done', color: 'var(--color-success)' },
  ];
  
  const columns = customColumns || defaultColumns;
  
  // Group items by status using mapped field and sort by view-specific sortOrder
  const itemsByStatus = useMemo(() => {
    if (!items || !Array.isArray(items)) return {};
    
    const grouped = items.reduce((acc, item) => {
      const status = getField(item, 'columnField') || 'active';
      if (!acc[status]) acc[status] = [];
      acc[status].push(item);
      return acc;
    }, {});
    
    // Sort each column's items by view-specific sortOrder (NEW)
    Object.keys(grouped).forEach(status => {
      grouped[status].sort((a, b) => {
        // Use view-specific sort order from viewMetadata
        const orderA = getSortOrder(a.id);
        const orderB = getSortOrder(b.id);
        
        // If both have order, sort by order
        if (orderA !== undefined && orderB !== undefined) {
          return orderA - orderB;
        }
        
        // If only one has order, prioritize it
        if (orderA !== undefined) return -1;
        if (orderB !== undefined) return 1;
        
        // Fallback to global cardOrder field (for backwards compatibility)
        const globalOrderA = getField(a, 'cardOrder');
        const globalOrderB = getField(b, 'cardOrder');
        if (globalOrderA !== undefined && globalOrderB !== undefined) {
          return globalOrderA - globalOrderB;
        }
        
        // Final fallback to creation time or id
        const timeA = a.createdAt || a.id;
        const timeB = b.createdAt || b.id;
        return timeA < timeB ? -1 : 1;
      });
    });
    
    return grouped;
  }, [items, getField, getSortOrder]);

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) {
      console.log('[KanbanView] Drag ended with no drop target');
      return;
    }
    if (active.id === over.id) return;

    // Check if we're moving between columns or within the same column
    const activeItem = items.find(item => item.id === active.id);
    if (!activeItem) {
      console.warn('[KanbanView] Could not find active item:', active.id);
      return;
    }
    
    // Get column IDs from drag data
    const activeColumnId = active.data.current?.columnId || getField(activeItem, 'columnField');
    const overColumnId = over.data.current?.type === 'card' 
      ? over.data.current?.columnId  // Dropped on a card - use its column
      : over.id;                      // Dropped on column area - use column id
    
    console.log('[KanbanView] Drag end:', {
      activeId: active.id,
      overId: over.id,
      overType: over.data.current?.type,
      activeColumnId,
      overColumnId
    });
    
    // If we're moving to a different column
    if (activeColumnId && overColumnId && activeColumnId !== overColumnId) {
      const statusField = fieldMappings['columnField'];
      const cardOrderField = fieldMappings['cardOrder'];
      
      if (statusField) {
        // Calculate the next cardOrder for the target column
        const targetColumnItems = items.filter(item => getField(item, 'columnField') === overColumnId);
        const maxOrder = targetColumnItems.reduce((max, item) => {
          const order = getField(item, 'cardOrder');
          return order !== undefined && order > max ? order : max;
        }, -1);
        
        // Update the item's status and cardOrder
        const updates = { [statusField]: overColumnId };
        if (cardOrderField) {
          updates[cardOrderField] = maxOrder + 1;
        }
        
        console.log('[KanbanView] Moving to different column:', {
          itemId: active.id,
          from: activeColumnId,
          to: overColumnId,
          updates
        });
        
        updateItem(active.id, updates);
        
        if (onItemUpdate) {
          onItemUpdate(active.id, 'status-changed', overColumnId);
        }
      }
    } else if (activeColumnId === overColumnId) {
      // Reordering within the same column (NEW: uses view-specific sortOrder)
      
      // Get fresh column items from current items array
      const columnItems = items.filter(item => getField(item, 'columnField') === activeColumnId);
      
      // Sort by current view-specific order
      columnItems.sort((a, b) => {
        const orderA = getSortOrder(a.id) ?? getField(a, 'cardOrder') ?? 0;
        const orderB = getSortOrder(b.id) ?? getField(b, 'cardOrder') ?? 0;
        return orderA - orderB;
      });
      
      const oldIndex = columnItems.findIndex(item => item.id === active.id);
      const newIndex = columnItems.findIndex(item => item.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) {
        console.warn('[KanbanView] Could not find items for reorder', { oldIndex, newIndex });
        return;
      }
      if (oldIndex === newIndex) return;
      
      // Reorder the array
      const reordered = [...columnItems];
      const [removed] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, removed);
      
      // Update view-specific sortOrder for all items in this column (NEW)
      // Use setTimeout to batch updates and prevent race conditions
      setTimeout(() => {
        reordered.forEach((item, index) => {
          setSortOrder(item.id, index);
        });
      }, 0);
      
      console.log('[KanbanView] Reordered within column:', activeColumnId, 'from', oldIndex, 'to', newIndex);
    }
  };

  const handleAddCard = (status) => {
    const titleField = fieldMappings['cardTitle'];
    const statusField = fieldMappings['columnField'];
    
    if (!titleField || !statusField) {
      console.warn('[KanbanView] Missing field mappings for cardTitle or columnField');
      return;
    }
    
    // Calculate the next sortOrder for this column (NEW: uses view-specific order)
    const columnItems = itemsByStatus[status] || [];
    const maxOrder = columnItems.reduce((max, item) => {
      const order = getSortOrder(item.id) ?? getField(item, 'cardOrder') ?? 0;
      return order > max ? order : max;
    }, -1);
    
    const newItem = {
      [titleField]: 'New Task',
      [statusField]: status,
    };
    
    // Add the item first
    const newId = addItem(newItem);
    
    // Then set view-specific sortOrder (NEW)
    if (newId) {
      setTimeout(() => {
        setSortOrder(newId, maxOrder + 1);
      }, 0);
    }
  };
  
  const handleCardClick = (item) => {
    if (onItemClick) {
      onItemClick(item);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      
      if (cmdOrCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo && undo) {
          undo();
        }
      } else if (cmdOrCtrl && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (canRedo && redo) {
          redo();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, undo, redo]);

  return (
    <div className="kanban-view" style={{ width: '100%', height: '100%' }}>
      <DndContext
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="kanban-board">
          {columns.map((column) => (
            <KanbanColumn
              key={column.id}
              id={column.id}
              title={column.title}
              color={column.color}
              items={itemsByStatus[column.id] || []}
              getField={getField}
              onAddCard={() => handleAddCard(column.id)}
              onCardClick={handleCardClick}
              columnStyles={columnStyles}
              cardStyles={cardStyles}
            />
          ))}
        </div>
        
        <DragOverlay>
          {activeId ? (
            <div className="kanban-card dragging" style={cardStyles}>
              {getField(items.find(item => item.id === activeId), 'cardTitle') || 'Task'}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KanbanView;
